import { useRouter } from 'next/router';
import { createContext, type PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useBusinessUnitContext } from '~/contexts/BusinessUnitContext/BusinessUnitContext';
import { useUTM } from '~/hooks';

import { EVENTS, type TEvents } from './constants';

export type TrackedFilterValue = {
  default: boolean;
  value: unknown;
};

export type JobFiltersTrackedValues = Partial<{
  job: string;
  city: string;
  distance: TrackedFilterValue;
  experience: string;
  sort: TrackedFilterValue;
  type: string;
  resultsNumber: number;
  pageNumber: number;
}>;

type JobOfferParams = { refId?: string; jobTitle?: string };
type ApplicationFunnelParams = JobOfferParams & { email: string };

type TrackingContextType = {
  sessionId: string;
  track: (name: TEvents, params?: Record<string, any>) => void;
  trackPage: (name: TEvents, params?: Record<string, any>) => void;
  trackActiveMissionPageChange: (params: Omit<JobFiltersTrackedValues, 'pageNumber'> & { pageNumber: number }) => void;
  trackJobOfferClicked: (params: JobFiltersTrackedValues & JobOfferParams) => void;
  trackSearchResultsDisplayed: (params: JobFiltersTrackedValues) => void;
  trackApplyButtonClicked: (params: JobOfferParams) => void;
  trackEmailSubmitted: (params: ApplicationFunnelParams) => void;
  trackApplicationSubmitted: (params: ApplicationFunnelParams) => void;
  utms: ReturnType<typeof useUTM>;
  referer: string | null;
};

export const TrackingContext = createContext<TrackingContextType>({
  sessionId: '',
  track: () => undefined,
  trackPage: () => undefined,
  trackApplyButtonClicked: () => undefined,
  trackEmailSubmitted: () => undefined,
  trackApplicationSubmitted: () => undefined,
  trackActiveMissionPageChange: () => undefined,
  trackJobOfferClicked: () => undefined,
  trackSearchResultsDisplayed: () => undefined,
  utms: {},
  referer: null,
});

export function TrackingContextProvider({ children }: PropsWithChildren) {
  const utms = useUTM();
  const { asPath } = useRouter();
  const sessionId = useMemo(() => uuidv4(), []);
  const { businessUnit, iframe } = useBusinessUnitContext();

  const [referer, setReferer] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<string | null>(null);

  useEffect(() => {
    setReferer(currentPage);
    setCurrentPage(`${window.location.origin}${globalThis.location?.pathname}`);

    if (iframe) return;
    window.analytics?.identify('default');
  }, [asPath, iframe]);

  const track = (name: TEvents, params?: Record<string, any>) => {
    if (iframe) return;

    window.analytics?.track(name, {
      sessionId,
      ...params,
      ...utms,
      businessUnit,
    });
  };

  const trackPage = (name: TEvents, params?: Record<string, any>) => {
    if (iframe) return;

    // at this point, the referer has not yet been updated
    // so the referer (previousPageUrl) is the value of the currentPage variable
    track(name, {
      pageUrl: `${window.location.origin}${window.location.pathname}`,
      previousPageUrl: currentPage,
      ...params,
    });
  };

  const trackActiveMissionPageChange = (
    params: Omit<JobFiltersTrackedValues, 'pageNumber'> & { pageNumber: number },
  ) => {
    track(EVENTS.activeMissionsPageChanged, params);
  };

  const trackJobOfferClicked = (params: JobFiltersTrackedValues & JobOfferParams) => {
    track(EVENTS.jobOfferClicked, params);
  };

  const trackSearchResultsDisplayed = (params: JobFiltersTrackedValues) => {
    track(EVENTS.searchResultsDisplayed, params);
  };

  const trackApplyButtonClicked = (params: JobOfferParams) => {
    track(EVENTS.applyButtonClicked, params);
  };
  const trackEmailSubmitted = (params: ApplicationFunnelParams) => {
    track(EVENTS.emailSubmitted, params);
  };
  const trackApplicationSubmitted = (params: ApplicationFunnelParams) => {
    track(EVENTS.applicationSubmitted, params);
  };

  return (
    <TrackingContext.Provider
      value={{
        sessionId,
        track,
        trackPage,
        trackActiveMissionPageChange,
        trackJobOfferClicked,
        trackSearchResultsDisplayed,
        trackApplyButtonClicked,
        trackEmailSubmitted,
        trackApplicationSubmitted,
        utms,
        referer,
      }}
    >
      {children}
    </TrackingContext.Provider>
  );
}

export const useTrackingContext = () => useContext(TrackingContext);
